import { useEffect, useRef, useState, type ReactNode } from "react";
import { ViewerClient } from "../client/ViewerClient";
import { ViewerProvider } from "../state/ViewerProvider";
import type { Catalogs } from "../shared/api-contract";

const VIEWER_ORIGIN =
  (import.meta.env.VITE_VIEWER_ORIGIN as string | undefined)?.replace(/\/$/, "") ??
  "http://localhost:5174";

interface HostRenderCtx {
  ready: boolean;
  client: ViewerClient | null;
  iframe: ReactNode;
  viewerOrigin: string;
}

/**
 * Owns the viewer <iframe> + the RPC `ViewerClient` for one viewer mode
 * (ring → /3dviewer.html, band → /band.html). The iframe is created once per
 * mount; the handshake (250ms ping) + `getCatalogs()` resolve `ready`. Wraps
 * children in `ViewerProvider` so any descendant can `useViewer()`.
 *
 * Studio and Band routes each mount their own host (navigating away disposes
 * the client; coming back re-handshakes — a few hundred ms, acceptable).
 */
export function ViewerHost({
  mode,
  mainviewer,
  children,
}: {
  mode: "ring" | "band" | "engagement";
  mainviewer?: boolean;
  children: (ctx: HostRenderCtx) => ReactNode;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [client, setClient] = useState<ViewerClient | null>(null);
  const [catalogs, setCatalogs] = useState<Catalogs | null>(null);
  const ready = !!client && !!catalogs;

  const routePath = mode === "band" ? "/band.html" : mode === "engagement" ? "/engagement.html" : "/3dviewer.html";
  // Dev passthrough: forward viewer boot params (?model / ?vjson / ?config) from
  // the host page URL into the iframe, so a viewer-local file can be opened in
  // the editor without a Vault model (see the /dev/studio route). Relative paths
  // resolve against the viewer origin, not this app.
  const search = new URLSearchParams();
  if (mainviewer) search.set("mainviewer", "true");
  if (mode === "ring") {
    const host = new URLSearchParams(window.location.search);
    for (const key of ["model", "vjson", "config"]) {
      const value = host.get(key);
      if (value) search.set(key, value);
    }
  }
  const qs = search.toString();
  const iframeSrc = `${VIEWER_ORIGIN}${routePath}${qs ? `?${qs}` : ""}`;

  useEffect(() => {
    if (!iframeRef.current) return;
    const c = new ViewerClient(iframeRef.current, VIEWER_ORIGIN);
    (window as unknown as { viewerClient: ViewerClient }).viewerClient = c;
    let alive = true;
    (async () => {
      await c.whenReady();
      const cat = await c.ring.getCatalogs();
      if (!alive) return;
      setClient(c);
      setCatalogs(cat);
    })();
    return () => {
      alive = false;
      c.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeSrc]);

  // The viewer document paints its own backdrop until the scene background goes
  // transparent; leaving the frame itself unpainted is what lets the editor
  // surface behind it show through when it does.
  const iframe = (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      title={mode === "band" ? "Wedding Band Viewer" : mode === "engagement" ? "Engagement Ring Viewer" : "3D Jewelry Viewer"}
      allow="xr-spatial-tracking; fullscreen"
      className="h-full w-full border-0"
      style={{ background: "transparent" }}
    />
  );

  return (
    <ViewerProvider client={client} catalogs={catalogs}>
      {children({ ready, client, iframe, viewerOrigin: VIEWER_ORIGIN })}
    </ViewerProvider>
  );
}
